import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  FiMessageCircle, FiMoon, FiSun, FiBookOpen, FiShoppingBag,
  FiUsers, FiAward, FiUser, FiLock, FiX, FiCheck, FiMenu,
  FiChevronLeft, FiChevronRight, FiHeart, FiCompass,
} from 'react-icons/fi';
import { BsMoonStarsFill } from 'react-icons/bs';

// ─── Sidebar navigation — mirrors SanctuaryLayout menu ──────────────────────

const MENU = [
  { key: 'archetypes', Icon: FiMoon,          label: 'Archetypes', locked: true  },
  { key: 'chat',       Icon: FiMessageCircle, label: 'Chat',       locked: false },
  { key: 'rituals',    Icon: FiSun,           label: 'Rituals',    locked: true  },
  { key: 'journal',    Icon: FiBookOpen,      label: 'Journal',    locked: true  },
  { key: 'shop',       Icon: FiShoppingBag,   label: 'Shop',       locked: true  },
  { key: 'community',  Icon: FiUsers,         label: 'Community',  locked: true  },
  { key: 'membership', Icon: FiAward,         label: 'Membership', locked: true  },
  { key: 'profile',    Icon: FiUser,          label: 'Profile',    locked: true  },
];

// Map emotion → response key
const EMOTION_FOCUS_MAP = {
  overwhelmed: 'stress',
  confused:    'life_direction',
  anxious:     'overthinking',
  hopeful:     'motivation',
  numb:        'grief',
  stuck:       'life_direction',
  curious:     'self_discovery',
  other:       'emotional_clarity',
};

const EMOTIONS = [
  { id: 'overwhelmed', label: 'Overwhelmed' },
  { id: 'confused',    label: 'Confused'    },
  { id: 'anxious',     label: 'Anxious'     },
  { id: 'hopeful',     label: 'Hopeful'     },
  { id: 'numb',        label: 'Numb'        },
  { id: 'stuck',       label: 'Stuck'       },
  { id: 'curious',     label: 'Curious'     },
  { id: 'other',       label: 'Something else' },
];

const DIRECTIONS =[
  { key: 'career_change',      
    label: 'Career & Identity',    
    question: 'What about your work life feels misaligned or uncertain right now?'            
  },

  { key: 'stress',             
    label: 'Stress & Overwhelm',   
    question: "What's been weighing on your mind lately that you can't seem to switch off?"      
  },

  { key: 'overthinking',       
    label: 'Overthinking',         
    question: 'What thought keeps replaying in your mind no matter how much you try to move past it?'      
  },

  { key: 'life_direction',     
    label: 'Life Direction',       
    question: "Where in your life do you feel uncertain about the path ahead?" 
  },

  { key: 'relationships',      
    label: 'Relationships',        
    question: "What relationship in your life has been sitting heavy on your heart lately?"          
  },

  { key: 'confidence',         
    label: 'Confidence & Worth',   
    question: 'What situation recently made you question yourself or your worth?'                  
  },

  { key: 'grief',              
    label: 'Grief & Loss',         
    question: "What loss or memory still lingers quietly in your thoughts?" 
  },

  { key: 'motivation',         
    label: 'Purpose & Motivation', 
    question: "What used to excite or motivate you that now feels distant or faded?"                     
  },

  { key: 'spiritual_guidance', 
    label: 'Spiritual Growth',     
    question: 'What deeper meaning or direction have you been searching for lately?'           
  },

  { key: 'emotional_clarity',  
    label: 'Something else',       
    question: "What's on your mind right now that you simply need space to think through?"      
  },
];

// || is used as paragraph separator in all response strings
const EMOTION_OPENERS = {
  overwhelmed: "What you're carrying is heavier than you've probably been letting on to most people around you.||",
  confused:    "The confusion you're feeling isn't a sign that you're lost — it's a sign that you're between chapters, and that the old answers no longer fit.||",
  anxious:     "That undercurrent of anxiety is your system flagging something important that hasn't yet been given full attention.||",
  hopeful:     "There's something significant about the hope you're still holding — despite everything. That hasn't given up on you.||",
  numb:        "Numbness is often the nervous system's quiet signal: I've been holding too much, for too long, without somewhere for it to go.||",
  stuck:       "Feeling stuck usually means two parts of you want different things — and neither one has been heard fully yet.||",
  curious:     "That curiosity is a compass. It's pointing somewhere most people don't follow. You did.||",
  other:       "Whatever you're carrying right now — it brought you here. And that's not a small thing.||",
};

const DIRECTION_OPENERS = {
  stress:             "You already know what's been pressing down on you. Let's not stay at the surface.||",
  overthinking:       "The loop you're caught in isn't random noise — it's knocking on a door that hasn't been opened yet.||",
  life_direction:     "That restlessness isn't confusion. It's the beginning of a new map taking shape.||",
  relationships:      "Relationships hold a mirror to everything we haven't yet resolved in ourselves. Let's look at what's really there.||",
  self_discovery:     "Most people never ask the question you're asking now. You're already ahead of where you think you are.||",
  confidence:         "What's made you question yourself deserves more than a pep talk — it deserves honesty.||",
  grief:              "Loss doesn't announce when it's finished with us. I'm here for however long yours has been staying.||",
  motivation:         "When the drive disappears, it's rarely about laziness. It's usually trying to tell you something important.||",
  career_change:      "Work and identity are harder to separate than anyone admits. What's stirring beneath the surface matters.||",
  spiritual_guidance: "That pull toward something deeper isn't a distraction — it's a direction worth following honestly.||",
  patterns:           "Patterns don't repeat by accident. They're the mind's way of trying to solve something it hasn't solved yet.||",
  future_self:        "The version of you that you're becoming is already informing the unrest you feel. Let's understand what they need.||",
  emotional_clarity:  "Whatever brought you here, it matters. I'm listening — not just to the words, but to what's underneath them.||",
  community:          "Feeling unseen is one of the quietest kinds of pain. What you're carrying around belonging deserves to be spoken.||",
};

const RESPONSES = {
  emotional_clarity:
    "Emotions rarely arrive with clear labels. They layer, contradict, and shift — and that disorientation doesn't mean anything is wrong with you. It means you're in the middle of something real.||The patterns emerging in what you've shared suggest that what you're feeling isn't noise. It's important information your inner world has been trying to send you — consistently, and for longer than you may have realized.||Most people try to manage their emotions. What actually transforms a person is learning to understand them. To give them names. To follow them to their source.||Andlegur will help you do exactly that — not through generic frameworks, but through a practice of honest self-inquiry built specifically around you.",

  career_change:
    "Career transitions feel terrifying because they touch something deeper than work — they question identity itself.||You're not confused about your career. You're in the middle of becoming someone new. That's not a problem to solve. It's an invitation to understand what actually matters to you beneath the expectations you've absorbed over the years.||The clarity you're searching for rarely comes from more information or other people's opinions. It comes from a conversation with yourself — one that most of us have been putting off.||That conversation starts here.",

  life_direction:
    "Feeling directionless isn't a flaw. It's often a signal that you've outgrown the map you were given — and a new one hasn't yet appeared.||The questions you're asking now aren't random. They're the result of growth you may not have fully acknowledged or honored yet.||Direction rarely arrives as a sudden announcement. It emerges quietly — through reflection, through noticing what consistently pulls at you, through honest conversations with yourself you actually finish.||You've already taken the first step by naming that you want more than drift.",

  stress:
    "Your nervous system has been working overtime trying to hold everything together. And it has been doing that for longer than you've probably admitted to yourself.||Stress at this level isn't simply about time management. It's about a deeper misalignment between what you're giving and what you're receiving — from your environment, your relationships, and perhaps most importantly, from yourself.||The rest you're actually looking for isn't a vacation. It's a new relationship with your own inner world — one where you're permitted to set things down without everything falling apart.||You don't have to push through this alone.",

  overthinking:
    "Your mind is doing exactly what it believes keeps you safe.||The loop isn't irrational — it's your intelligence turned inward with no outlet and no resolution. You've probably noticed that more thinking doesn't resolve it. The answer isn't more analysis. It's developing the capacity to step outside the loop entirely and observe it from a distance.||You're not too much. Your mind is simply searching for somewhere to land. Somewhere that can hold the full weight of what you're carrying without judgment.||That is exactly what Andlegur exists to become for you.",

  spiritual_guidance:
    "Something deeper is calling — and you were right to listen.||Spiritual growth rarely announces itself with clarity. More often it arrives as restlessness. A hunger. A growing sense that the surface of things is no longer enough.||You don't need the right system or the right teacher before you begin. You need a practice of honest inner listening — in a space where your questions are welcomed and not answered too quickly.||Your spiritual path is entirely your own. Andlegur won't tell you what to believe. It will help you hear what, at some level, you already know.",

  self_discovery:
    "The question you're carrying — who am I beneath the roles I perform and the stories I've been told? — is the most important one a person can ask.||The answer is never a single thing. It shifts, deepens, and surprises. But the practice of asking it honestly — without flinching — is itself a transformation.||What's emerging here is that you are already more self-aware than you give yourself credit for. The next layer isn't about uncovering something broken. It's about recognizing something whole that's been there all along.||You're closer to yourself than you think.",

  motivation:
    "Motivation disappears when something more fundamental shifts — the sense of why.||When the surface reasons fall away, what remains is the essential question: what do you actually care about, underneath the achievement, the approval, and the habits of momentum?||You haven't lost your drive. It's redirected inward, asking to be recalibrated against something more aligned with who you are now — not who you were when you first started.||This isn't stagnation. It's the quiet before a more intentional chapter begins.",

  community:
    "Feeling unseen or fundamentally misunderstood is one of the most quietly painful human experiences — and among the least talked about.||You can be surrounded by people and still feel profoundly alone. That gap between presence and actual connection is real, and it deserves to be taken seriously.||What you're truly searching for isn't just company — it's resonance. The kind of connection where you don't have to edit yourself in order to belong.||You are not alone in wanting that. And you will not be alone in building it.",

  relationships:
    "Relationships hold a mirror to everything we haven't yet resolved within ourselves — and that's exactly what makes them both painful and irreplaceable.||The patterns showing up across your connections aren't accidents. They're invitations to understand something about yourself that hasn't yet been seen clearly.||Andlegur doesn't offer relationship advice. It offers something more foundational: the kind of self-understanding that changes every relationship in your life by changing the most fundamental one — the one you have with yourself.||That's where real, lasting relational change begins.",

  confidence:
    "What you've named isn't ordinary self-doubt. It's the accumulated weight of moments where you felt invisible, dismissed, or somehow not enough — moments you weren't given space to fully process.||Confidence doesn't build through willpower or performance. It comes from going back to those moments with honesty, understanding what actually happened, and releasing what was never yours to carry.||You are not the story you've been told about yourself. You never were.||Andlegur will help you remember who you were before that story began.",

  grief:
    "Grief doesn't follow a timeline, and it rarely announces when it's finished with you.||What you've experienced — whether loss of a person, a chapter of life, a version of yourself, or a future you expected — deserves space that the world rarely allows.||Healing isn't returning to who you were before. It's becoming someone who has integrated the full depth of what they've felt — and found that they're still standing.||You're allowed to take exactly as long as you need.",

  patterns:
    "Beneath your daily experience, emotional patterns have been quietly running — shaping how you respond, what you avoid, and what you're drawn toward without always knowing why.||Most people move through their entire lives without ever naming these patterns clearly. You're taking a different path.||What's beginning to emerge from what you've shared suggests a coherent set of patterns — ones that have served you in some ways and limited you in others, often without you fully realizing either.||This is precisely where Andlegur goes deeper than anything you've tried before.",

  future_self:
    "The version of you that you sense in quiet moments — clearer, more grounded, more fully inhabiting your own life — that person isn't a fantasy.||They're a direction. An invitation. A possibility that exists right now, waiting for your honest engagement.||The gap between who you are today and who you're becoming isn't a failure to close. It's the journey — the most meaningful one you'll ever take.||Your future self isn't waiting for you to have everything figured out. They're waiting for you to begin. You just did.",
};

// ─── Keyword-based response detection ───────────────────────────────────────

const KEYWORD_MAP = [
  { key: 'stress',             words: ['stress', 'stressed', 'overwhelm', 'overwhelmed', 'exhausted', 'burned out', 'burnout', 'too much', "can't cope", 'breaking point', 'pressure', 'falling apart'] },
  { key: 'overthinking',       words: ['overthink', 'spiral', "can't stop thinking", 'ruminate', 'anxiety', 'anxious', 'worry', 'worrying', 'what if', 'racing thoughts', 'obsessing', 'in my head'] },
  { key: 'grief',              words: ['grief', 'grieving', 'loss', 'death', 'died', 'missing', 'gone', 'mourn', 'heartbroken', 'bereaved', 'lost someone'] },
  { key: 'relationships',      words: ['relationship', 'partner', 'boyfriend', 'girlfriend', 'husband', 'wife', 'marriage', 'breakup', 'broke up', 'divorce', 'friends', 'family', 'lonely', 'loneliness', 'alone', 'connection', 'trust'] },
  { key: 'confidence',         words: ['confidence', 'self-doubt', 'not enough', 'worthless', 'worth', 'insecure', 'imposter', 'failure', 'shame', 'ashamed', 'embarrassed', 'rejected', 'inadequate', 'useless'] },
  { key: 'motivation',         words: ['motivation', 'meaning', 'meaningless', 'pointless', "don't care", 'unmotivated', 'no drive', 'lost my drive', 'why bother', 'stagnant', 'going through motions'] },
  { key: 'career_change',      words: ['career', 'job', 'quit', 'fired', 'unemployed', 'promotion', 'workplace', 'office', 'profession', 'work life', 'business'] },
  { key: 'life_direction',     words: ['direction', "don't know what", 'what to do', 'future', 'path', 'next step', 'crossroads', 'change my life', 'no idea', 'drifting', 'lost', 'confused'] },
  { key: 'spiritual_guidance', words: ['spiritual', 'soul', 'faith', 'god', 'universe', 'meditation', 'awakening', 'higher purpose', 'believe', 'energy', 'deeper meaning', 'calling'] },
  { key: 'self_discovery',     words: ['who am i', 'identity', 'authentic', 'true self', 'discover myself', 'understand myself', 'find myself', 'searching for', 'my values', 'my purpose'] },
  { key: 'community',          words: ['belong', 'belonging', 'fit in', 'outsider', 'excluded', 'isolated', 'no friends', 'misunderstood', 'invisible', 'unseen', 'tribe'] },
  { key: 'patterns',           words: ['pattern', 'same pattern', 'keep repeating', 'cycle', 'same thing again', 'self-sabotage', 'sabotage', 'always end up', 'keep doing this'] },
  { key: 'future_self',        words: ['future self', 'who i want to be', 'becoming', 'my potential', 'version of me', 'next chapter', 'transform', 'evolve', 'growth'] },
];

function detectResponseKey(text, selectedEmotion, selectedDirection, inputMode) {
  // Direction mode with explicit selection — use it directly
  if (inputMode === 'directions' && selectedDirection) return selectedDirection;

  const lower = text.toLowerCase();
  const scores = {};

  for (const { key, words } of KEYWORD_MAP) {
    scores[key] = 0;
    for (const word of words) {
      if (lower.includes(word)) scores[key]++;
    }
  }

  const [bestKey, bestScore] = Object.entries(scores).reduce(
    (best, cur) => cur[1] > best[1] ? cur : best,
    ['', 0]
  );

  if (bestScore > 0) return bestKey;
  if (selectedEmotion && EMOTION_FOCUS_MAP[selectedEmotion]) return EMOTION_FOCUS_MAP[selectedEmotion];
  return 'emotional_clarity';
}

// ─── Reflector Avatar ────────────────────────────────────────────────────────

function ReflectorAvatar() {
  return (
    <div className="shrink-0 w-10 h-10 rounded-full bg-linear-to-br from-indigo-700/70 to-indigo-600/40 border border-indigo-400/50 flex items-center justify-center shadow-lg shadow-indigo-600/30">
      <BsMoonStarsFill className="w-4 h-4 text-indigo-100" />
    </div>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function ExperiencePage() {
  const navigate = useNavigate();
  const [inputText, setInputText]             = useState('');
  const [selectedEmotion, setSelectedEmotion] = useState('');
  const [phase, setPhase]                         = useState('idle'); // 'idle' | 'thinking' | 'response'
  const [showModal, setShowModal]                 = useState(false);
  const [sidebarOpen, setSidebarOpen]             = useState(false);
  const [mdExpanded, setMdExpanded]               = useState(false);
  const [showChatHint, setShowChatHint]           = useState(true);
  const [inputMode, setInputMode]                 = useState('feelings'); // 'feelings' | 'directions'
  const [selectedDirection, setSelectedDirection] = useState('');
  const [resolvedKey, setResolvedKey]             = useState('emotional_clarity');
  const [resolvedOpener, setResolvedOpener]       = useState('');
  const [showClarityModal, setShowClarityModal]   = useState(false);

  // Auto-show join modal 5s after response appears
  useEffect(() => {
    if (phase !== 'response') return;
    const t = setTimeout(() => setShowModal(true), 5000);
    return () => clearTimeout(t);
  }, [phase]);

  // Auto-hide chat guide hint after 4s
  useEffect(() => {
    if (!showChatHint) return;
    const t = setTimeout(() => setShowChatHint(false), 4000);
    return () => clearTimeout(t);
  }, [showChatHint]);

  const handleSubmit = async () => {
    if (inputText.trim().length < 10) return;
    // Detect the best response key from keywords + selection
    const key = detectResponseKey(inputText, selectedEmotion, selectedDirection, inputMode);
    const opener = inputMode === 'directions'
      ? (DIRECTION_OPENERS[key] || DIRECTION_OPENERS.emotional_clarity)
      : (EMOTION_OPENERS[selectedEmotion] || '');
    setResolvedKey(key);
    setResolvedOpener(opener);
    setPhase('thinking');
    await new Promise(r => setTimeout(r, 3200));
    setPhase('response');
  };

  // Build response paragraphs from the key + opener resolved at submit time
  const paragraphs = (resolvedOpener + (RESPONSES[resolvedKey] || RESPONSES.emotional_clarity))
    .split('||')
    .filter(Boolean);

  return (
    <div className="relative min-h-screen overflow-hidden flex" style={{ backgroundColor: '#05091B' }}>
      <Helmet>
        <title>Try Andlegur Free — AI-Guided Reflection &amp; Self-Discovery</title>
        <meta name="description" content="Experience Andlegur's AI reflection companion. Share what's on your mind and receive personalized guidance. No account needed." />
        <link rel="canonical" href="https://andlegur.com/try" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      {/* ── Atmospheric background — same as sanctuary ── */}
      <div className="fixed inset-0 z-0 pointer-events-none" aria-hidden="true">
       
        <div
          className="absolute inset-0"
          style={{ background: 'radial-gradient(ellipse 70% 80% at 30% 50%, rgba(5,9,27,0.82) 0%, rgba(5,9,27,0.35) 65%, rgba(5,9,27,0.97) 100%)' }}
        />
      </div>

      {/* ── Mobile hamburger toggle ── */}
      <div className="md:hidden fixed top-4 left-4 z-50">
        <button
          onClick={() => setSidebarOpen(prev => !prev)}
          className="w-12 h-12 bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 rounded-xl shadow-xl flex items-center justify-center transition-all duration-300 ring-2 ring-indigo-400/40 hover:ring-indigo-300/60"
          aria-label={sidebarOpen ? 'Close menu' : 'Open menu'}
        >
          {sidebarOpen
            ? <FiX className="w-5 h-5 text-white" />
            : <FiMenu className="w-5 h-5 text-white" />
          }
        </button>
      </div>

      {/* ── Mobile backdrop overlay ── */}
      {sidebarOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/50 z-30 transition-opacity duration-300"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ════════════════════════════════════════════════════════════════════
          SIDEBAR — toggle on mobile/md, fixed expanded on lg
      ════════════════════════════════════════════════════════════════════ */}
      <aside className={`
        fixed left-0 top-0 h-full z-40 flex flex-col
        w-24 ${mdExpanded ? 'md:w-48' : 'md:w-22'} lg:w-56
        bg-linear-to-b from-indigo-950/30 to-[#05091B]
        backdrop-blur-md
        border-r border-indigo-700/40
        transition-all duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0
      `}>

        {/* MD expand/collapse chevron */}
        <button
          className={`hidden md:flex lg:hidden items-center justify-center w-8 h-16 rounded-l-full absolute top-1/2 right-0 -translate-y-1/2 z-50 border border-indigo-700/50 shadow-lg
            ${mdExpanded ? 'bg-indigo-600 text-white hover:bg-indigo-500' : 'bg-indigo-900/80 hover:bg-indigo-800 text-indigo-300'}`}
          style={{ borderRight: 'none' }}
          onClick={() => setMdExpanded(prev => !prev)}
          aria-label={mdExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
        >
          {mdExpanded
            ? <FiChevronLeft className="w-5 h-5" />
            : <FiChevronRight className="w-5 h-5" />
          }
        </button>

        {/* Logo row */}
        <div className="flex items-center gap-3 px-3 lg:px-4 py-5 border-b border-indigo-700/30 min-h-18">
          <div className="w-10 h-10 shrink-0 rounded-full ring-2 ring-indigo-500/50 shadow-md shadow-indigo-600/20 flex items-center justify-center overflow-hidden">
            <img src="/assets/images/logo b7.webp" alt="Andlegur" className="w-full h-full object-contain" />
          </div>
          <div className={`flex-col leading-none hidden lg:flex ${mdExpanded ? 'md:flex' : ''}`}>
            <span className="font-serif font-bold text-base bg-linear-to-r from-indigo-200 via-indigo-100 to-indigo-300 bg-clip-text text-transparent tracking-wide">
              Andlegur
            </span>
            <span className="text-xs text-indigo-400 font-sans tracking-wider mt-0.5">Clarity Starts Here</span>
          </div>
        </div>

        {/* Nav items */}
        <nav className="flex-1 px-2 py-4 overflow-y-auto">
          <div className="flex flex-col gap-1 border border-indigo-500/50 rounded-xl bg-linear-to-br from-indigo-900/80 to-indigo-900/50 p-2">
            {MENU.map(({ key, Icon, label, locked }) => {
              const isActive = key === 'chat';
              return (
                <button
                  key={key}
                  onClick={() => { if (locked) setShowModal(true); setSidebarOpen(false); }}
                  title={label}
                  className={`w-full flex flex-col md:flex-row md:items-center md:gap-3 items-center p-2.5 py-3.5 rounded-xl transition-all duration-200 group ${
                    isActive
                      ? 'bg-linear-to-br from-indigo-700/60 to-indigo-600/30 ring-2 ring-indigo-400/30  shadow-sm shadow-indigo-600/20'
                      : 'text-indigo-300 hover:text-indigo-100 hover:bg-indigo-800/40'
                  }`}
                >
                  <div className={`w-10 h-10 flex items-center justify-center shrink-0 mb-1 md:mb-0 rounded-lg transition-colors ${
                    isActive ? 'text-indigo-50' : 'text-indigo-300 group-hover:text-indigo-200'
                  }`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <span className={`text-sm sm:text-base font-sans font-medium leading-tight text-center md:text-left md:hidden lg:block ${mdExpanded ? 'md:block' : ''} ${isActive ? 'text-indigo-50' : 'text-indigo-300 group-hover:text-indigo-200'}`}>
                    {label}
                  </span>
                  {locked && (
                    <FiLock className={`w-3 h-3 ml-auto opacity-50 shrink-0 hidden lg:block ${mdExpanded ? 'md:block' : ''}`} />
                  )}
                </button>
              );
            })}
          </div>
        </nav>

        {/* CTA box */}
        <div className="p-3 border-t border-indigo-700/30">
          <div className={`bg-linear-to-br from-indigo-900/60 to-indigo-800/30 border border-indigo-600/40 rounded-xl p-3 text-center hidden lg:block ${mdExpanded ? 'md:block' : ''}`}>
            <p className="text-xs text-indigo-300 font-sans leading-snug mb-2 font-medium">
              Preview mode
            </p>
            <button
              onClick={() => setShowModal(true)}
              className="w-full text-xs bg-indigo-600 hover:bg-indigo-500 text-white py-2 rounded-lg font-sans font-semibold transition-colors shadow-sm shadow-indigo-600/30"
            >
              Join Free →
            </button>
          </div>
          {/* Compact lock icon when sidebar is narrow */}
          <button
            onClick={() => setShowModal(true)}
            className={`w-full flex items-center justify-center py-1 lg:hidden ${mdExpanded ? 'md:hidden' : ''}`}
            title="Join Free"
          >
            <div className="w-10 h-10 rounded-xl bg-indigo-800/60 border border-indigo-600/50 flex items-center justify-center shadow-sm">
              <FiLock className="w-4 h-4 text-indigo-300" />
            </div>
          </button>
        </div>
      </aside>

      {/* ════════════════════════════════════════════════════════════════════
          MAIN CONTENT — mirrors SanctuaryLayout main
      ════════════════════════════════════════════════════════════════════ */}
      <main className={`flex-1 relative z-10 ${mdExpanded ? 'md:ml-48' : 'md:ml-22'} lg:ml-56 min-h-screen flex flex-col px-6 md:px-12 py-8 pb-20 transition-all duration-300`}>
        <div className="max-w-6xl mx-auto w-full pt-20 md:pt-24 lg:pt-0">

          {/* ── Company Branding — exact SanctuaryLayout copy ── */}
          <div className="mb-8 text-center">
            <div className="flex items-center justify-center gap-4 mb-3">
              <div className="w-12 h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center ring-2 ring-indigo-400/30 shadow-lg backdrop-blur-sm">
                <img
                  src="/assets/images/logo b7.webp"
                  alt="Andlegur Logo"
                  className="w-10 h-10 md:w-14 md:h-14 object-contain"
                />
              </div>
              <div className="flex flex-col">
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-serif bg-linear-to-r from-indigo-200 via-indigo-100 to-indigo-300 bg-clip-text text-transparent font-bold tracking-wide">
                  Andlegur
                </h1>
                <p className="text-indigo-400 text-sm md:text-base font-medium font-sans -mt-1">
                  Too much on your mind?
                </p>
              </div>
            </div>
            <div className="w-32 h-0.5 bg-linear-to-r from-transparent via-indigo-500 to-transparent mx-auto opacity-60" />
            <p className="text-sm md:text-base text-indigo-300 font-sans font-normal leading-relaxed max-w-2xl mx-auto opacity-90 mt-4 mb-2">
              Write your thoughts. Discover deeper insights about yourself.
            </p>
          </div>

          {/* ── Page Header ── */}
          <header className="mb-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
              
              {/* Preview badge */}
              <div className="flex items-center gap-2 bg-indigo-900/60 border border-indigo-700/50 rounded-full px-4 py-2">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-xs text-indigo-300 font-sans uppercase tracking-wider">Preview Mode · Free to try</span>
              </div>
            </div>
          </header>

          {/* ── Content section ── */}
          <section className="min-h-[48vh] bg-indigo-900/20 border border-indigo-700/60 rounded-2xl p-4 md:p-6 shadow-xl shadow-indigo-950/50">
            <div className="max-w-2xl mx-auto">

              {/* Mode toggle — top right, only during idle phase */}
              {phase === 'idle' && (
                <div className="flex justify-end mb-4">
                  <div className="flex items-center gap-1 bg-indigo-950/70 border border-indigo-700/50 rounded-xl p-1">
                    <button
                      onClick={() => { setInputMode('feelings'); setSelectedDirection(''); }}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold font-sans transition-all duration-200 ${
                        inputMode === 'feelings'
                          ? 'bg-indigo-600 text-white shadow-sm'
                          : 'text-indigo-400 hover:text-indigo-200'
                      }`}
                    >
                      <FiHeart className="w-3 h-3" /> Feelings
                    </button>
                    <button
                      onClick={() => { setInputMode('directions'); setSelectedEmotion(''); }}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold font-sans transition-all duration-200 ${
                        inputMode === 'directions'
                          ? 'bg-indigo-600 text-white shadow-sm'
                          : 'text-indigo-400 hover:text-indigo-200'
                      }`}
                    >
                      <FiCompass className="w-3 h-3" /> Directions
                    </button>
                  </div>
                </div>
              )}

              {/* Reflector greeting — changes based on mode */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-6">
                <div className="flex sm:block items-center gap-2 mb-1 sm:mb-0">
                  <ReflectorAvatar />
                </div>
                <div className="bg-linear-to-br from-indigo-900/50 to-indigo-900/25 border border-indigo-600/50 rounded-2xl rounded-tl-none px-5 py-4 flex-1 shadow-inner shadow-indigo-900/40">
                  <p className="text-[10px] text-indigo-200 uppercase tracking-widest mb-2 font-sans">Reflector</p>
                  <AnimatePresence mode="wait">
                    {inputMode === 'feelings' ? (
                      <motion.p
                        key="greeting-feelings"
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={{ duration: 0.25 }}
                        className="text-indigo-100 text-[15px] leading-relaxed"
                      >
                        Welcome. I'm <span className="text-indigo-300 font-semibold">Reflector</span>. Here to listen and help you explore what's on your mind.

Share a thought or choose a feeling below.
                      </motion.p>
                    ) : (
                      <motion.p
                        key="greeting-directions"
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={{ duration: 0.25 }}
                        className="text-indigo-100 text-[15px] leading-relaxed"
                      >
                       We all carry things on our minds.
                      Which area of your life feels most present right now? Choose one below and share what's on your mind.
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              {/* ── IDLE: Write + picker ── */}
              {phase === 'idle' && (
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  {/* Mode-aware picker */}
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={inputMode}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.22 }}
                      className="mb-4"
                    >
                      {inputMode === 'feelings' ? (
                        <>
                          <p className="text-indigo-100 text-xs font-sans uppercase tracking-widest mb-2.5">
                            How are you feeling right now?
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {EMOTIONS.map(({ id, label }) => (
                              <button
                                key={id}
                                onClick={() => setSelectedEmotion(prev => prev === id ? '' : id)}
                                className={`text-sm px-3.5 py-1.5 rounded-full border transition-all duration-200 font-sans ${
                                  selectedEmotion === id
                                    ? 'bg-indigo-600 border-indigo-400/80 text-indigo-100 ring-1 ring-indigo-400/30 shadow-sm shadow-indigo-600/30'
                              : 'bg-purple-600 border-indigo-300/70 text-white hover:border-indigo-500/80 hover:text-indigo-200 hover:bg-indigo-800/40'
                                }`}
                              >
                                {label}
                              </button>
                            ))}
                          </div>
                        </>
                      ) : (
                        <>
                          <p className="text-indigo-100 text-xs font-sans uppercase tracking-widest mb-2.5">
                            What do you need help with right now?
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {DIRECTIONS.map(({ key, label }) => (
                              <button
                                key={key}
                                onClick={() => setSelectedDirection(prev => prev === key ? '' : key)}
                                className={`text-sm px-3.5 py-1.5 rounded-full border transition-all duration-200 font-sans ${
                                  selectedDirection === key
                                    ? 'bg-indigo-600 border-indigo-400/80 text-indigo-100 ring-1 ring-indigo-400/30 shadow-sm shadow-indigo-600/30'
                                    : 'bg-indigo-900/40 border-indigo-300/50 text-indigo-200 hover:border-indigo-500 hover:text-indigo-200 hover:bg-indigo-800/40'
                                }`}
                              >
                                {label}
                              </button>
                            ))}
                          </div>
                          <AnimatePresence>
                            {selectedDirection && (
                              <motion.p
                                key={selectedDirection}
                                initial={{ opacity: 0, y: 4 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                className="mt-3 text-sm text-indigo-300 italic font-sans leading-relaxed pl-3 border-l-2 border-indigo-600/50"
                              >
                                {DIRECTIONS.find(d => d.key === selectedDirection)?.question}
                              </motion.p>
                            )}
                          </AnimatePresence>
                        </>
                      )}
                    </motion.div>
                  </AnimatePresence>

                  {/* Micro prompt */}
                  {inputMode === 'feelings' && (
                    <p className="text-indigo-500 text-xs font-sans italic mb-1.5">Start with one sentence:</p>
                  )}

                  {/* Textarea */}
                  <textarea
                    value={inputText}
                    onChange={e => { if (e.target.value.length <= 530) setInputText(e.target.value); }}
                    placeholder={inputMode === 'directions' && selectedDirection
                      ? DIRECTIONS.find(d => d.key === selectedDirection)?.question ?? "Lately I\u2019ve been feeling…"
                      : "Lately I\u2019ve been feeling…"}
                    className="w-full bg-indigo-900/60 border border-indigo-700/70 rounded-xl px-5 py-4 text-indigo-100 placeholder:text-indigo-500 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 resize-none text-[15px] leading-relaxed mb-1 font-sans shadow-inner shadow-indigo-950/50"
                    rows={5}
                    maxLength={530}
                  />

                  {/* Character counter — appears near the limit */}
                  {inputText.length >= 400 && (
                    <div className={`text-xs font-medium text-right mb-2 font-sans ${
                      inputText.length > 500 ? 'text-red-400' :
                      inputText.length >= 450 ? 'text-yellow-400' : 'text-indigo-400'
                    }`}>
                      {500 - inputText.length < 0 ? `${inputText.length - 500} over limit` : `${500 - inputText.length} left`}
                    </div>
                  )}

                  {/* Footer row */}
                  <div className="flex flex-col gap-2 mt-2">
                    <button
                      onClick={handleSubmit}
                      disabled={inputText.trim().length < 10}
                      className="w-full bg-indigo-600 hover:bg-indigo-500 disabled:bg-indigo-700/60 disabled:text-indigo-400 disabled:cursor-not-allowed text-white px-6 py-3 rounded-xl font-sans font-semibold text-base transition-all duration-200 hover:scale-[1.02] shadow-lg"
                    >
                      Discover Insight →
                    </button>
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-1.5 text-xs text-green-300 font-sans">
                        <FiLock className="w-3 h-3" /> Your reflections stay private.
                      </span>
                      <span className="text-xs text-indigo-500 font-sans">Takes less than 30 seconds</span>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* ── THINKING ── */}
              {phase === 'thinking' && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex flex-col sm:flex-row gap-2 sm:gap-3"
                >
                  <div className="flex sm:block items-center gap-2 mb-1 sm:mb-0">
                    <ReflectorAvatar />
                  </div>
                  <div className="bg-linear-to-br from-indigo-800/50 to-indigo-700/25 border border-indigo-600/50 rounded-2xl rounded-tl-none px-5 py-4 flex-1 shadow-inner shadow-indigo-900/40">
                    <p className="text-[10px] text-indigo-300 uppercase tracking-widest mb-3 font-sans">Reflector</p>
                    <div className="flex items-center gap-2">
                      {[0, 1, 2].map(i => (
                        <motion.div
                          key={i}
                          className="w-2.5 h-2.5 rounded-full bg-indigo-400"
                          animate={{ y: [0, -7, 0], opacity: [0.4, 1, 0.4] }}
                          transition={{ duration: 0.9, delay: i * 0.22, repeat: Infinity }}
                        />
                      ))}
                      <span className="text-indigo-400 text-sm italic ml-1 font-sans">Reflecting on what you've shared…</span>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* ── RESPONSE ── */}
              {phase === 'response' && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex flex-col sm:flex-row gap-2 sm:gap-3"
                >
                  <div className="flex sm:block items-center gap-2 mb-1 sm:mb-0">
                    <ReflectorAvatar />
                  </div>
                  <div className="bg-linear-to-br from-indigo-800/50 to-indigo-700/25 border border-indigo-600/50 rounded-2xl rounded-tl-none px-5 py-5 flex-1 shadow-inner shadow-indigo-900/40">
                    <p className="text-[10px] text-indigo-300 uppercase tracking-widest mb-4 pb-3 border-b border-indigo-600/40 font-sans">Reflector</p>

                    <div className="space-y-4 mb-5">
                      {paragraphs.map((para, i) => (
                        <motion.p
                          key={i}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.55, delay: i * 0.55 }}
                          className="text-indigo-100 text-[15px] leading-relaxed"
                        >
                          {para}
                        </motion.p>
                      ))}
                    </div>

                    {/* Blurred teaser */}
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: paragraphs.length * 0.55 + 0.8 }}
                      className="pt-4 border-t border-indigo-600/40"
                    >
                      <div className="relative rounded-xl overflow-hidden mb-4">
                        <p
                          className="blur-sm select-none pointer-events-none text-indigo-200 text-sm leading-relaxed font-sans"
                          aria-hidden="true"
                        >
                          Reflector has identified a recurring emotional pattern in what you've shared. Your personalised 7-day practice — three daily prompts calibrated to exactly where you are right now — is ready and waiting…
                        </p>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="flex items-center gap-1.5 text-xs font-semibold text-indigo-300 font-sans bg-[#070d23]/95 border border-indigo-700/60 px-3 py-1.5 rounded-full uppercase tracking-widest">
                            <FiLock className="w-3 h-3" /> Free account unlocks your full journey
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => setShowClarityModal(true)}
                        className="w-full bg-indigo-600 hover:bg-indigo-500 text-white py-3.5 rounded-xl font-sans font-semibold text-sm transition-all duration-200 hover:scale-[1.01] shadow-lg"
                      >
                        Unlock More Insights — It's Free →
                      </button>
                    </motion.div>
                  </div>
                </motion.div>
              )}

            </div>
          </section>

        </div>
      </main>

      {/* ── ChatGuide hint — floats once on page load ── */}
      <AnimatePresence>
        {showChatHint && phase === 'idle' && (
          <motion.div
            key="chat-hint"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.35 }}
            className="fixed left-1/2 -translate-x-1/2 bottom-28 md:bottom-32 z-30 pointer-events-none"
          >
            <div className="bg-indigo-600/90 text-white px-4 py-2 rounded-lg backdrop-blur-sm border border-indigo-500 text-sm font-sans flex items-center gap-2 shadow-lg whitespace-nowrap">
              Share what's on your heart, freely...
              <motion.span
                animate={{ y: [0, 4, 0] }}
                transition={{ duration: 1, repeat: Infinity }}
                className="text-indigo-100"
              >
                ↓
              </motion.span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ════════════════════════════════════════════════════════════════════
          CLARITY MODAL — triggered by main CTA after response
      ════════════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {showClarityModal && (
          <motion.div
            key="clarity-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center px-4"
          >
            <motion.div
              className="absolute inset-0 bg-[#050912]/90 backdrop-blur-md"
              onClick={() => setShowClarityModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 40 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 40 }}
              transition={{ type: 'spring', damping: 28, stiffness: 280 }}
              className="relative w-full max-w-md bg-[#070c1e] border border-indigo-600/70 rounded-3xl px-7 pt-8 pb-9 shadow-2xl shadow-indigo-950/90 z-10 overflow-hidden"
            >
              {/* Top glow */}
              <div
                className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-28 rounded-full pointer-events-none"
                style={{ background: 'radial-gradient(circle, rgba(99,102,241,0.22) 0%, transparent 70%)' }}
                aria-hidden="true"
              />

              <button
                onClick={() => setShowClarityModal(false)}
                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-indigo-900/60 border border-indigo-700/50 flex items-center justify-center text-indigo-400 hover:text-indigo-200 hover:bg-indigo-800/60 transition-all"
                aria-label="Close"
              >
                <FiX className="w-4 h-4" />
              </button>

              {/* Moon icon */}
              <div className="flex justify-center mb-5">
                <div className="w-16 h-16 rounded-full bg-linear-to-br from-indigo-800/80 to-indigo-900/60 border border-indigo-500/50 flex items-center justify-center shadow-lg shadow-indigo-600/20">
                  <BsMoonStarsFill className="w-7 h-7 text-indigo-300" />
                </div>
              </div>

              <h2 className="text-2xl font-serif font-bold text-indigo-50 text-center mb-3 leading-snug">
                Unlock My Insights
              </h2>

              <p className="text-indigo-300 text-sm text-center leading-relaxed mb-6 font-sans">
                You've started something meaningful.<br />
                <strong className="text-indigo-100">Login or create your free Andlegur space</strong> in under 30 seconds to save your reflection and continue discovering more insights about yourself.
              </p>

              <div className="flex items-center justify-center gap-2 bg-indigo-900/40 border border-indigo-700/40 rounded-full px-4 py-2 mb-6 font-sans">
                <FiCheck className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                <span className="text-xs text-indigo-300">Free to start · Private · No credit card</span>
              </div>

              <button
                onClick={() => navigate('/register')}
                className="w-full bg-indigo-600 hover:bg-indigo-500 text-white py-4 rounded-2xl font-semibold text-base transition-all duration-200 hover:scale-[1.02] shadow-xl mb-3 font-sans"
              >
                Create My Private Space →
              </button>

              <button
                onClick={() => navigate('/login')}
                className="w-full border border-indigo-700/60 text-indigo-300 hover:bg-indigo-900/50 hover:text-indigo-100 py-3 rounded-2xl text-sm transition-all duration-200 font-sans"
              >
                Already have an account
              </button>

              <p className="text-center text-xs text-indigo-600 mt-4 font-sans">
                Not ready?{' '}
                <button
                  onClick={() => setShowClarityModal(false)}
                  className="text-indigo-500 hover:text-indigo-300 underline transition-colors"
                >
                  Keep exploring
                </button>
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ════════════════════════════════════════════════════════════════════
          JOIN MODAL
      ════════════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            key="modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center px-4"
          >
            <motion.div
              className="absolute inset-0 bg-[#050912]/85 backdrop-blur-md"
              onClick={() => setShowModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 60 }}
              transition={{ type: 'spring', damping: 26, stiffness: 260 }}
              className="relative w-full max-w-md bg-[#070c1e] border border-indigo-700/70 rounded-3xl px-7 pt-7 pb-9 shadow-2xl shadow-indigo-950/80 z-10 overflow-hidden"
            >
              {/* Glow */}
              <div
                className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-60 h-24 rounded-full pointer-events-none"
                style={{ background: 'radial-gradient(circle, rgba(99,102,241,0.15) 0%, transparent 70%)' }}
                aria-hidden="true"
              />

              <button
                onClick={() => setShowModal(false)}
                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-indigo-900/60 border border-indigo-700/50 flex items-center justify-center text-indigo-400 hover:text-indigo-200 hover:bg-indigo-800/60 transition-all"
                aria-label="Close"
              >
                <FiX className="w-4 h-4" />
              </button>

              <div className="flex justify-center mb-5">
                <div className="w-16 h-16 rounded-full bg-indigo-900/80 border border-indigo-500/50 flex items-center justify-center shadow-lg">
                  <BsMoonStarsFill className="w-7 h-7 text-indigo-300" />
                </div>
              </div>

              <h2 className="text-xl font-serif font-bold text-indigo-50 text-center mb-2">
                Your reflection deserves a place to live.
              </h2>
              <p className="text-indigo-300 text-sm text-center leading-relaxed mb-5 font-sans">
                You’ve taken the first step toward understanding your thoughts.
                Create your free Andlegur space in under 30 seconds to save your reflection and continue exploring deeper insights.
              </p>

              <div className="flex items-center justify-center gap-2 bg-indigo-900/40 border border-indigo-700/40 rounded-full px-4 py-2 mb-6 font-sans">
                <FiCheck className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-xs text-indigo-300">Free to start · Private · No credit card</span>
              </div>

              <button
                onClick={() => navigate('/register')}
                className="w-full bg-indigo-600 hover:bg-indigo-500 text-white py-4 rounded-2xl font-semibold text-base transition-all duration-200 hover:scale-[1.02] shadow-xl mb-3 font-sans"
              >
                Create My Space — It's Free →
              </button>

              <button
                onClick={() => navigate('/login')}
                className="w-full border border-indigo-700/60 text-indigo-300 hover:bg-indigo-900/50 hover:text-indigo-100 py-3 rounded-2xl text-sm transition-all duration-200 font-sans"
              >
                I already have an account
              </button>

              <p className="text-center text-xs text-indigo-600 mt-4 font-sans">
                Not ready?{' '}
                <button
                  onClick={() => setShowModal(false)}
                  className="text-indigo-400 hover:text-indigo-200 underline transition-colors"
                >
                  Keep exploring (preview only)
                </button>
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
}

